import React, { useMemo } from 'react';

import { ClickAwayListener, styled, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';

import OhsModal from '../../modal/OhsModal';

export interface OhsTooltipProps {
  message: string | React.ReactNode;
  backgroundColor?: string;
  textColor?: string;
  iconColor?: string;
  children?: React.ReactElement<any, any>;
}

const HtmlTooltip = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ),
  {
    shouldForwardProp(propName) {
      return propName !== 'backgroundColor' && propName !== 'textColor';
    },
  }
)<{ backgroundColor?: string; textColor?: string }>(({ theme, backgroundColor, textColor }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: backgroundColor || theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: backgroundColor || theme.palette.common.white,
    color: textColor || theme.palette.common.black,
    fontSize: '13px',
    padding: '8px',
    lineHeight: '18px',
    border: '1px solid #d4d4d5',
    boxShadow: '0 2px 4px 0 rgba(34, 36, 38, 0.12), 0 2px 10px 0 rgba(34, 36, 38, 0.15)',
  },
}));

export default function OhsTooltip(props: OhsTooltipProps) {
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);

  function extractTextFromReactNode(node: React.ReactNode): string {
    if (typeof node === 'string' || typeof node === 'number') {
      return node.toString();
    }

    if (Array.isArray(node)) {
      return node.map(extractTextFromReactNode).join('');
    }

    if (React.isValidElement(node)) {
      return extractTextFromReactNode(node.props.children);
    }

    return '';
  }

  const descriptionLength = useMemo(
    () => extractTextFromReactNode(props.message).length,
    [props.message]
  );

  const isLongDescription = useMemo(() => descriptionLength > 200, [descriptionLength]);

  const handleIconClick = () => {
    if (isLongDescription) {
      setModalOpen(true);
    } else {
      setTooltipOpen(!tooltipOpen);
    }
  };

  const handleClickAway = () => {
    if (!isLongDescription) {
      setTooltipOpen(false);
    }
  };

  return (
    <>
      {props.children || !isLongDescription ? (
        <ClickAwayListener onClickAway={handleClickAway}>
          <HtmlTooltip
            open={tooltipOpen}
            title={props.message}
            placement="top"
            arrow
            backgroundColor={props.backgroundColor}
            textColor={props.textColor}
            onOpen={() => handleIconClick()} // Open on hover
            onClose={() => handleIconClick()} // Close on hover leave
          >
            {props.children ? (
              props.children
            ) : (
              <HelpOutlinedIcon
                onClick={handleIconClick}
                sx={{ fontSize: '16px', color: props.iconColor, cursor: 'pointer' }}
              />
            )}
          </HtmlTooltip>
        </ClickAwayListener>
      ) : (
        <HelpOutlinedIcon
          onClick={handleIconClick}
          sx={{ fontSize: '16px', color: '#1678c2', cursor: 'pointer' }}
        />
      )}
      <OhsModal
        title=""
        open={modalOpen}
        setModalOpenFunc={setModalOpen}
        ContentNode={<div style={{ padding: '16px' }}>{props.message}</div>}
      />
    </>
  );
}

OhsTooltip.defaultProps = {
  iconColor: 'gray',
  children: undefined,
  backgroundColor: 'white',
  textColor: 'black',
};
